import { Link } from "react-router-dom";
import { Keyboard as KIcon, Trophy, GraduationCap, Sparkles, ArrowRight } from "lucide-react";
import Keyboard from "../components/Keyboard";
import HomeContent from "../components/HomeContent";

export default function Landing() {
    return (
        <div className="relative overflow-hidden">
            {/* Hero */}
            <section className="relative">
                <div className="hero-grid absolute inset-0 -z-10" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-16 sm:pb-20 lg:pt-24 lg:pb-28">
                    <div className="grid lg:grid-cols-12 gap-10 sm:gap-12 items-center">
                        <div className="lg:col-span-7 space-y-6 sm:space-y-8">
                            <div className="inline-flex items-center gap-2 eyebrow px-3 py-1.5 rounded-full border border-[var(--lt-border)] bg-[var(--lt-bg-2)]/60">
                                <Sparkles size={12} className="text-[var(--lt-terracotta)]" />
                                Bilingual typing tutor · English & नेपाली
                            </div>
                            <h1
                                className="text-3xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1]"
                                style={{
                                    fontFamily:
                                        '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                Type with{" "}
                                <span className="text-[var(--lt-terracotta)]">flow</span>
                                <br />
                                <span
                                    style={{
                                        fontFamily:
                                            '"Noto Sans Devanagari", "Plus Jakarta Sans", sans-serif',
                                        fontWeight: 500,
                                    }}
                                >
                                    टाइप गर्नुहोस्{" "}
                                    <span className="text-[var(--lt-amber)]">प्रवाह</span>{" "}
                                    सहित
                                </span>
                            </h1>
                            <p className="text-base sm:text-lg text-[var(--lt-muted)] max-w-xl leading-relaxed">
                                Structured lessons, distraction-free practice, and timed tests for
                                both{" "}
                                <span className="text-[var(--lt-text)] font-semibold">English</span>{" "}
                                and{" "}
                                <span className="text-[var(--lt-text)] font-semibold font-nepali">
                                    Traditional Nepali (देवनागरी)
                                </span>
                                . No OS switch needed — just start typing.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link
                                    to="/practice"
                                    className="btn-terra inline-flex items-center gap-2"
                                    data-testid="hero-start-practicing-btn"
                                >
                                    Start Practicing <ArrowRight size={16} />
                                </Link>
                                <Link
                                    to="/test"
                                    className="btn-ghost-line inline-flex items-center gap-2"
                                    data-testid="hero-take-test-btn"
                                >
                                    Take a Speed Test
                                </Link>
                            </div>
                            <div className="grid grid-cols-3 gap-4 max-w-md pt-4">
                                <Stat n="2" l="Languages" />
                                <Stat n="12" l="Lessons" />
                                <Stat n="∞" l="Practice Runs" />
                            </div>
                        </div>

                        <div className="lg:col-span-5">
                            <div className="glass rounded-2xl p-6 grain">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="eyebrow">Traditional Nepali Preview</span>
                                    <span className="text-xs text-[var(--lt-muted)]">देवनागरी</span>
                                </div>
                                <Keyboard language="nepali" activeCode={null} nextChar="न" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="max-w-7xl mx-auto px-6 py-16">
                <div className="grid md:grid-cols-3 gap-6">
                    <Feature
                        icon={<GraduationCap className="text-[var(--lt-emerald)]" />}
                        title="Structured Lessons"
                        text="From home-row basics to full paragraphs in both scripts. Progress at your own pace."
                    />
                    <Feature
                        icon={<KIcon className="text-[var(--lt-terracotta)]" />}
                        title="Live Keyboard Guide"
                        text="On-screen keyboard highlights the next key and Devanagari mapping in real time."
                    />
                    <Feature
                        icon={<Trophy className="text-[var(--lt-amber)]" />}
                        title="Tests & Leaderboard"
                        text="30s / 60s / 120s tests. Ranks split by English and Traditional Nepali WPM."
                    />
                </div>
            </section>

            <HomeContent />
        </div>
    );
}

function Stat({ n, l }) {
    return (
        <div>
            <div className="font-mono-stat text-3xl text-[var(--lt-text)]">{n}</div>
            <div className="eyebrow mt-1">{l}</div>
        </div>
    );
}

function Feature({ icon, title, text }) {
    return (
        <div className="glass rounded-2xl p-6 hover:border-[var(--lt-terracotta)] transition-colors">
            <div className="w-10 h-10 rounded-lg bg-[var(--lt-bg-2)] flex items-center justify-center mb-4">
                {icon}
            </div>
            <h3 className="font-heading text-xl font-bold mb-2">{title}</h3>
            <p className="text-sm text-[var(--lt-muted)] leading-relaxed">{text}</p>
        </div>
    );
}
